import * as Sentry from '@sentry/browser';
import { parse, stringify } from 'qs';
import { RouteComponentProps } from 'react-router';

import { Asset, createClient } from 'contentful';
import { useEffect, useState } from 'react';
import { AdAgent } from 'src/api/contentful/contentTypes.cloverdotcom';
import { fetchAsset, fetchEntries } from 'src/api/contentful/fetchEntries';
import { readCookie, setCookie } from 'src/utils/Cookies';
import { get, getField } from 'src/utils/EntryUtil';
import { Environment, bankPersonalizationJwtPublicKey } from 'src/utils/Environment';
import { decodeToken } from 'src/utils/Jwt';
import { getUrlParameter, isUrl } from 'src/utils/Urls';
import { LanguageCode } from '../../utils/Locales';

export const BANK_PERSONALIZATION_QUERY_PARAM = 'partnerInfo';
export const BANK_PERSONALIZATION_COOKIE_NAME = 'BANK_PERSONALIZATION';
export const AD_AGENT_QUERY_PARAM = 'adAgent';
export const AD_AGENT_COOKIE_NAME = 'AD_AGENT';

export interface BankPersonalizationInfo {
  partnerName?: string;
  partnerLogo?: string;
  iat?: number;
  asset?: any;
}

const extractPartnerTokenFromUrl = (search: string) => {
  const { partnerInfo, ...additionalParams } = parse(search, { ignoreQueryPrefix: true });
  window.history.replaceState({}, document.title, `${window.location.origin}${window.location.pathname}${stringify(additionalParams, { addQueryPrefix: true })}`);
  return { partnerInfo: (partnerInfo as string) };
};

const decode = (token: string) => {
  try {
    return decodeToken(token, bankPersonalizationJwtPublicKey) as BankPersonalizationInfo;
  } catch (err) {
    console.warn('Error decoding bank personalization token', err);
    const e = new Error(err.message);
    e.stack = err.stack;
    e.name = 'Error decoding bank personalization token';
    Sentry.captureException(e);
    return;
  }
};

const readAdAgentFromUrlAndSetCookie = () => {
  const adAgentEntryId = getUrlParameter(AD_AGENT_QUERY_PARAM);
  if (!adAgentEntryId) return;

  setCookie(AD_AGENT_COOKIE_NAME, adAgentEntryId);
  return adAgentEntryId;
};

const readJwtFromUrlAndSetCookie = (search: string) => {
  const { partnerInfo } = extractPartnerTokenFromUrl(search);
  if (!partnerInfo) return;

  setCookie(BANK_PERSONALIZATION_COOKIE_NAME, partnerInfo);
  return decode(partnerInfo);
};

const readJwtFromCookie = () => {
  const cookieValue = readCookie(BANK_PERSONALIZATION_COOKIE_NAME);
  if (!cookieValue) return;

  return decode(cookieValue);
};

export const useBankPersonalizationInternal = (history: RouteComponentProps['history']) => {
  const [ bankPersonalization, setBankPersonalization ] = useState<BankPersonalizationInfo>();
  useEffect(() => {
    const adAgentEntryId = readAdAgentFromUrlAndSetCookie() || readCookie(AD_AGENT_COOKIE_NAME);

    if (adAgentEntryId) {
      fetchAdAgentEntry(adAgentEntryId).then(entry => {
        const info = {
          sub: getField(entry, 'agentId'),
          partnerName: getField(entry, 'name'),
          partnerLogo: getField(entry, 'logo'),
        };
        setBankPersonalization(info);
      });
    } else {
      const bankPersonalization = readJwtFromUrlAndSetCookie(history.location.search) || readJwtFromCookie() || undefined;
      resolveAssetIdToUrl(bankPersonalization).then(value => {
        setBankPersonalization(value);
      });
    }

  }, [ history ]);
  return { bankPersonalization, setBankPersonalization };
};

const resolveAssetIdToUrl = async (bankPersonalization: BankPersonalizationInfo | undefined) => {
  if (!bankPersonalization || !bankPersonalization.partnerLogo || isUrl(bankPersonalization.partnerLogo)) {
    return Promise.resolve(bankPersonalization);
  }

  const partnerLogo = bankPersonalization.partnerLogo;

  let asset: Asset | undefined;

  try {
    asset = await fetchShopAsset(partnerLogo);
  } catch (err) {
    addBreadcrumb('Shop', partnerLogo);
  }

  if (!asset) {
    try {
      asset = await fetchDotcomAsset(partnerLogo);
    } catch (err) {
      addBreadcrumb('Dotcom', partnerLogo);
    }
  }

  if (!asset) {
    const errorMessage = `Unable to retrieve asset ${partnerLogo} from Shop and Dotcom Contentful`;
    Sentry.captureException(errorMessage);
    return {
      ...bankPersonalization,
      partnerLogo: undefined,
    };
  }

  return {
    ...bankPersonalization,
    partnerLogo: asset && asset.fields && asset.fields.file && asset.fields.file.url,
    asset,
  };
};

const fetchShopAsset = async (partnerLogo: string) => {
  return fetchAsset(partnerLogo!);
};

const fetchDotcomAsset = async (partnerLogo: string) => {
  const contentfulApi = createClient(Environment.getCloverdotcomContentfulConfig());
  return fetchAsset(partnerLogo, LanguageCode.ENGLISH, contentfulApi);
};

const fetchAdAgentEntry = async (adAgentEntryId: string) => {
  const contentfulApi = createClient(Environment.getCloverdotcomContentfulConfig());
  let adAgent;
  try {
    adAgent = await fetchEntries<AdAgent>(LanguageCode.ENGLISH, {
      filters: { sys: { id: adAgentEntryId } },
      include: 2,
    }, contentfulApi);

  } catch (err) {
    addBreadcrumb('Ad Agent Id', adAgentEntryId);
  }

  const [ entry ] = get(adAgent, 'items', []);
  return entry;
};

const addBreadcrumb = (contentfulSpace: string, partnerLogo: string) => {
  Sentry.addBreadcrumb({
    category: 'assets',
    message: `Unable to retrieve asset from ${contentfulSpace} Contentful space\n`,
    data: {
      assetId: partnerLogo,
    },
    level: Sentry.Severity.Info,
  });
};

export type BankPersonalization = ReturnType<typeof useBankPersonalizationInternal>;
